import clsx from 'clsx';
import React, { Fragment, useEffect, useState } from 'react';
import { ErrorCode, useDropzone } from 'react-dropzone';

import { Button } from '../button';
import { LoadingProgressBar } from '../loadingProgressBar';
import style from './fileUploader.module.scss';

export type FileTypes = 'image' | 'cv' | 'video' | 'csv' | 'bulkClients' | 'other';

const allowedFileTypes = (uploadType: FileTypes): any => {
  if (uploadType === 'video') {
    return {
      'video/quicktime': ['.mov'],
      'video/mp4': ['.mp4'],
      'video/x-ms-wmv': ['.wmv'],
      'video/x-msvideo': ['.avi']
    };
  } else if (uploadType === 'cv') {
    return {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc', '.docx'],
      'text/plain': ['.txt'],
      'image/png': ['.png']
    };
  } else if (uploadType === 'image') {
    return {
      'image/png': ['.png'],
      'image/jpg': ['.jpg'],
      'image/jpeg': ['.jpeg']
    };
  } else if (uploadType === 'csv' || uploadType === 'bulkClients') {
    return {
      'text/csv': ['.csv']
    };
  } else if (uploadType === 'other') {
    return {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc', '.docx'],
      'image/jpg': ['.jpg'],
      'image/jpeg': ['.jpeg'],
      'image/png': ['.png'],
      'image/heic': ['.heic']
    };
  }
};

const allowedFileSizes = (uploadType: FileTypes): number => {
  if (uploadType === 'video') {
    return 314572800;
  } else if (uploadType === 'cv') {
    return 31457280;
  } else if (uploadType === 'image') {
    return 3145728; //3MB
  } else if (uploadType === 'csv') {
    return 1145728; //1MB
  } else if (uploadType === 'bulkClients') {
    return 1145728; //1MB
  } else if (uploadType === 'other') {
    return 10485759; //10MB - 1byte
  }
  return 0;
};

const formatToMb = (bytes: number) => {
  return (bytes / 1048576).toFixed(0) + 'MB';
};

function randomInteger(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function getErrorMessageToDisplay(errType: string, uploadType: FileTypes) {
  let errMessageToDisplay: string | null = null;

  switch (errType) {
    case ErrorCode.FileInvalidType:
      if (uploadType === 'other') {
        errMessageToDisplay = `Invalid file format.;Accepted formats: JPG, JPEG, PNG, HEIC, PDF, DOC, DOCX.`;
      } else {
        errMessageToDisplay = `Invalid file type.; file type must be ${
          uploadType === 'bulkClients' ? '.csv' : uploadType
        }`;
      }
      break;
    case ErrorCode.FileTooLarge:
      if (uploadType === 'other') {
        errMessageToDisplay = `Invalid file size:; file must be less than 10MB.`;
      } else {
        errMessageToDisplay = `Invalid file size:; file must be ${formatToMb(
          allowedFileSizes(uploadType)
        )} or less.`;
      }
      break;
    case ErrorCode.FileTooSmall:
      errMessageToDisplay = 'File is too small for upload.;';
      break;

    default:
      errMessageToDisplay = 'File cannot be uploaded.;';
      break;
  }
  return errMessageToDisplay;
}

interface FileUploaderProps {
  handleFileUploaded?: any;
  uploadType: FileTypes;
  isLoading?: boolean;
  showProgressBarScreen?: boolean;
}

const FileUploader: React.FunctionComponent<FileUploaderProps> = ({
  showProgressBarScreen,
  handleFileUploaded,
  uploadType,
  isLoading = false
}) => {
  const [progressPercent, setProgressPercent] = useState<number>(14);
  const [delay, setDelay] = useState(1000);

  const { getRootProps, getInputProps, open, acceptedFiles, fileRejections } = useDropzone({
    noKeyboard: true,
    maxSize: allowedFileSizes(uploadType),
    minSize: 10,
    accept: allowedFileTypes(uploadType)
  });

  const fileRejectionItems = fileRejections.map(({ file, errors }: any) => {
    return (
      <div key={file.path}>
        <div>
          <div className={style.errorMessage}>
            {' '}
            <span className={clsx('material-icons')}>error_outline</span>
            {errors.map((e: any) => {
              const errMessageToDisplay = getErrorMessageToDisplay(e.code, uploadType);
              return (
                <Fragment key={e.code}>
                  <span className={clsx(style.errorMessageBold)}>
                    {errMessageToDisplay?.split(';')[0]}
                  </span>
                  {errMessageToDisplay?.split(';')[1]}
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>
    );
  });

  useEffect(() => {
    if (handleFileUploaded) {
      handleFileUploaded(acceptedFiles[0]);
    }
  }, [acceptedFiles]);

  //PROGRESSBAR Pseudo animation
  useEffect(() => {
    if (!showProgressBarScreen) return;

    const interval = setInterval(() => {
      setDelay(randomInteger(500, 5000));
      if (progressPercent < 100) {
        setProgressPercent((prev: any) => {
          const increment = Math.floor(delay / 500);
          const max = 100;

          if (prev + increment < max) {
            return prev + increment;
          } else {
            return max;
          }
        });
      }
    }, delay);
    return () => clearInterval(interval);
  }, [showProgressBarScreen]);

  return (
    <>
      <div
        className={clsx(
          style.dropzoneContainer,
          showProgressBarScreen && style.dropzoneContainerWhenUploading
        )}
      >
        {showProgressBarScreen && (
          <>
            <div className={style.loadingArea}>
              <LoadingProgressBar
                completed={!isLoading ? 100 : progressPercent}
                theme={'secondary'}
              />
              <div className={style.uploadText}>Uploading file...</div>
            </div>
          </>
        )}

        {!showProgressBarScreen && (
          <div
            {...getRootProps({ className: 'dropzone' })}
            className={clsx(style.dropzoneDragArea)}
            onClick={open}
          >
            <input {...getInputProps()} />

            <div
              className={clsx(
                style.dropzoneIcon,
                uploadType === 'video' ? style.iconVideo : style.iconCV
              )}
            >
              {' '}
              <span className="material-icons">upload</span>
            </div>
            <div className={style.dropzoneParagraph}>
              Drop your{' '}
              {uploadType === 'bulkClients'
                ? '.csv '
                : uploadType === 'other'
                ? ''
                : `${uploadType} `}
              file here or
            </div>

            <div className={style.uploadButtonContainer}>
              <Button
                size="small"
                theme="secondary"
                text="Select file"
                handleOnClick={undefined}
                customStyleClass={style.btnUpload}
              />
            </div>
            <div className={clsx(style.dropzoneParagraph, style.dropzoneParagraphNotice)}>
              {uploadType === 'image' && (
                <div>Format must be JPEG, JPG, or PNG. File size must be less than 3MB.</div>
              )}
            </div>
          </div>
        )}

        <aside className={style.asideContainer}>
          {/* <h4 className={style.acceptedFiles}>{files.length > 0 && 'Uploading file.. '}</h4>
          <ul>{files}</ul> */}

          <ul className={style.rejectedFiles}>{fileRejectionItems}</ul>
        </aside>
      </div>
    </>
  );
};

export default FileUploader;
